<template>
  <div class="flex h-[100dvh] flex-row">
    <ClientOnly>
      <ImpersonationBanner />
    </ClientOnly>
    <div class="flex flex-1 min-w-0 flex-col h-full min-h-0">
      <!-- Agent / admin nav (full portal). Guests on indexable public
           surfaces get the locked AccessTopNav instead. Pages that
           render their own header (notably PropertyPageShell on
           /projects/<dev>/<slug>) self-suppress this via the
           `isProjectDetailPage` check. -->
      <!-- AccessTopNav must render server-side so guests (incl. crawlers)
           see the nav in the SSR HTML. PortalTopNav stays inside ClientOnly
           because it depends on the auth store which only hydrates on
           the client. -->
      <AccessTopNav
        v-if="showGuestTopNav"
        :level="'guest'"
        @sign-in="onGuestSignin"
        @sign-up="onGuestSignup"
      />
      <ClientOnly>
        <PortalTopNav v-if="isAgentOrAdmin" />
        <MobileHeader
          v-else-if="!showGuestTopNav"
          class="desktop:hidden desktop:ps-6 desktop:pe-10"
        />
      </ClientOnly>
      <main
        data-scroll-container
        class="flex flex-1 min-h-0 flex-col overflow-y-auto overflow-x-clip"
        :class="isAgentOrAdmin ? 'pb-[88px] desktop:pb-0' : ''"
      >
        <div class="flex-1">
          <div :class="mainContentClass">
            <ClientOnly>
              <PortalTrialBanner v-if="isAgentOrAdmin" />
            </ClientOnly>
            <slot />
          </div>
        </div>
        <!-- /ask is full-bleed and self-contained; the global footer would
             push the chat off-viewport and add a vertical scroll. -->
        <!-- Phones: the guest top nav has no room for the language menu and <Footer> is
             desktop-only, so guests get it in its own row here. -->
        <div
          v-if="showMobileLocaleRow"
          class="flex justify-center py-6 tablet:hidden"
        >
          <LocaleMenu placement="top" />
        </div>
        <Footer v-if="!isFullBleedPage" />
      </main>
      <ClientOnly>
        <PortalBottomTabBar v-if="isAgentOrAdmin" />
        <!-- Self-gates: agents/leads without a valid WhatsApp number. -->
        <PortalPhoneCaptureModal v-if="isAgentOrAdmin" />
      </ClientOnly>
    </div>
    <CommonAuthPromptModal />
    <!-- Global Ask Insiders palette (Cmd/Ctrl+K). Self-gates on auth + route
         so it stays inert on /ask itself and on unauthed pages. -->
    <AskPaletteHost />
    <!-- Floating Ask launcher on every signed-in page; Ask has no nav tab. -->
    <AskLauncher v-if="isAgentOrAdmin" />
    <ExperimentFreeTierTour />
    <BillingPricingModal v-if="!isUaeAgent" />
    <ClientOnly>
      <BillingUaeGateSheet v-if="isAgentOrAdmin" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import AccessTopNav from '@/components/property/access/AccessTopNav.vue';
import { useOrganizationSchema, useWebsiteSchema } from '~/composables/useSchemaOrg';
import { useRedirectAfterAuth } from '@/composables/useRedirectAfterAuth';
import { PLAN_LINK_MAP, isPlanLinkKey } from '~/utils/billing/planLinkKeys';
import { stripLocalePrefix } from '~/utils/i18n/locale';

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

const isAgentOrAdmin = computed(() => !!authStore.user);

// Routes where guests should see the locked-style top nav (AccessTopNav).
// Project-detail pages render their own AccessTopNav inside PropertyPageShell
// — suppress here to avoid double headers.
const isProjectDetailPage = computed(() => {
  const p = stripLocalePrefix(route.path || '');
  return p.startsWith('/projects/') || p.startsWith('/properties/') || p.startsWith('/p/');
});

// Default-on for guests, opt-out on the few surfaces that render their
// own header (project detail) or aren't user-facing (404, auth flows).
const showGuestTopNav = computed(() => {
  if (isAgentOrAdmin.value) return false;
  if (isProjectDetailPage.value) return false;
  const p = stripLocalePrefix(route.path || '');
  // Auth, masterclass funnel, ask, and saas paths self-render their
  // chrome. Everything else (including /, /explore, /developers,
  // /developer-vault, /services, /news, /lp/*) gets the guest top nav.
  if (
    p.startsWith('/auth') ||
    p.startsWith('/login') ||
    p.startsWith('/signup') ||
    p.startsWith('/reset') ||
    p.startsWith('/masterclass') ||
    p.startsWith('/ask') ||
    p.startsWith('/saas') ||
    p === '/__nuxt_error'
  )
    return false;
  return true;
});

const { buildRedirectUrl } = useRedirectAfterAuth();
const onGuestSignin = () => navigateTo(buildRedirectUrl('/auth/login'));
const onGuestSignup = () => navigateTo(buildRedirectUrl('/auth/register'));

// Site-wide JSON-LD foundations. Drive the knowledge-panel logo + sitelinks
// search box in Google SERP. Emitted once per layout render and de-duped
// via the unique head keys inside the composables.
useOrganizationSchema();
useWebsiteSchema();

const isExplorePage = computed(() => {
  if (!route) return false;

  const routeName = route.name?.toString() || '';
  const routePath = route.path || '';

  const isExplore = routeName.includes('explore') || routePath.includes('/explore');

  return isExplore;
});

// /ask is full-bleed (the page draws its own 268px sidebar + main column)
// so we don't want the layout's default horizontal padding.
const isFullBleedPage = computed(() => {
  if (!route) return false;
  const routePath = route.path || '';
  // /ask and the AI Marketing studio render their own full-width chrome inside
  // the portal wireframe — no extra content padding, no global footer.
  return routePath === '/ask' || routePath.startsWith('/ask/') || routePath.startsWith('/marketing');
});

// Guest pages whose header is AccessTopNav — the layout's own, or PropertyPageShell's on project
// detail pages — hide its language menu below tablet width; see the row above <Footer>.
// Only when there is a choice to offer: an English-only deploy gets no empty row.
const { canSwitchLocale } = useLocaleSwitch();
const showMobileLocaleRow = computed(
  () =>
    canSwitchLocale.value &&
    !isFullBleedPage.value &&
    (showGuestTopNav.value || (isProjectDetailPage.value && !isAgentOrAdmin.value)),
);

const mainContentClass = computed(() => {
  const classes: string[] = [];
  if (!isFullBleedPage.value) {
    classes.push('desktop:ps-6');
    if (!isExplorePage.value) {
      classes.push('desktop:pe-6');
    }
  }
  return classes;
});

// Admin-generated Pro/Team payment links land on `?upgrade=<key>` (after the
// auto-login redirect). Open the pricing popup pre-selected to that plan, then
// strip the query param so a refresh/back doesn't re-open it.
const { openModal: openBillingModal } = useBillingModal();
const { isUae: isUaeAgent } = useUaeGate();

const maybeOpenUpgrade = () => {
  if (!import.meta.client) return;
  const key = route.query.upgrade;
  if (!isPlanLinkKey(key)) return;
  openBillingModal({ plan: PLAN_LINK_MAP[key] });
  const rest = { ...route.query };
  delete rest.upgrade;
  router.replace({ path: route.path, query: rest });
};

onMounted(maybeOpenUpgrade);
watch(() => route.query.upgrade, maybeOpenUpgrade);
</script>
